import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    previewImport, 
    importProductsMaster,
    importPricingMaster,
    importPackagingMaster,
    importSpecificationsMaster,
    importMediaMaster,
    importCategoryTaxonomy,
    importLookupReference,
    validateImport,
    clearSampleData
} from '../lib/api';
import { useAuth } from '../lib/auth';
import {
    Upload,
    FileXls,
    FileCsv,
    CheckCircle,
    Warning,
    X,
    ArrowRight,
    Trash,
    DownloadSimple,
    Info,
    Database
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';

const TABLE_TYPES = [
    { value: 'products_master', label: 'Products Master', description: 'Base product table - import first', required: true },
    { value: 'pricing_master', label: 'Pricing Master', description: 'Multiple prices per product allowed' },
    { value: 'packaging_master', label: 'Packaging Master', description: 'One record per product' },
    { value: 'specifications_master', label: 'Specifications Master', description: 'One record per product' },
    { value: 'media_master', label: 'Media Master', description: 'Multiple media per product allowed' },
    { value: 'category_taxonomy', label: 'Category Taxonomy', description: 'Category hierarchy' },
    { value: 'lookup_reference', label: 'Lookup Reference', description: 'Reference mappings & lookups' },
];

const IMPORT_FUNCTIONS = {
    products_master: importProductsMaster,
    pricing_master: importPricingMaster,
    packaging_master: importPackagingMaster,
    specifications_master: importSpecificationsMaster,
    media_master: importMediaMaster,
    category_taxonomy: importCategoryTaxonomy,
    lookup_reference: importLookupReference,
};

export default function DataImport() {
    const { isAdmin } = useAuth();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [tableType, setTableType] = useState('products_master');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [importResults, setImportResults] = useState([]);
    const [validationResult, setValidationResult] = useState(null);

    useEffect(() => {
        if (!isAdmin()) {
            navigate('/');
        }
    }, [isAdmin, navigate]);

    const handleFileSelect = async (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        const ext = selectedFile.name.split('.').pop()?.toLowerCase();
        if (!['csv', 'xlsx', 'xls'].includes(ext)) {
            toast.error('Please select a CSV or Excel file');
            return;
        }

        setFile(selectedFile);
        setLoading(true);

        try {
            const response = await previewImport(selectedFile, tableType);
            setPreview(response.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to preview file');
            setFile(null);
            setPreview(null);
        } finally {
            setLoading(false);
        }
    };

    const handleImport = async () => {
        if (!file) {
            toast.error('Please select a file');
            return;
        }

        // Validate product_key column exists
        if (!preview?.has_product_key && tableType !== 'category_taxonomy' && tableType !== 'lookup_reference') {
            toast.error('File must have a product_key column');
            return;
        }

        setLoading(true);
        try {
            const importFn = IMPORT_FUNCTIONS[tableType];
            const response = await importFn(file);
            
            const result = {
                ...response.data,
                timestamp: new Date().toISOString(),
                filename: file.name
            };
            
            setImportResults(prev => [result, ...prev]);
            toast.success(`Import complete: ${result.created} created, ${result.updated} updated`);
            
            // Reset for next import
            resetFile();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Import failed');
        } finally {
            setLoading(false);
        }
    };

    const handleValidate = async () => {
        setLoading(true);
        try {
            const response = await validateImport();
            setValidationResult(response.data);
            if (response.data.validation_passed) {
                toast.success('All data validated successfully');
            } else {
                toast.warning('Validation found some issues');
            }
        } catch (error) {
            toast.error('Validation failed');
        } finally {
            setLoading(false);
        }
    };

    const handleClearData = async () => {
        setLoading(true);
        try {
            await clearSampleData();
            toast.success('All sample data cleared');
            setImportResults([]);
            setValidationResult(null);
        } catch (error) {
            toast.error('Failed to clear data');
        } finally {
            setLoading(false);
        }
    };

    const resetFile = () => {
        setFile(null);
        setPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const downloadErrorReport = (result) => {
        if (!result.errors?.length) return;
        
        const csv = [
            ['Row', 'Error', 'Severity', 'Product Key'],
            ...result.errors.map(e => [e.row, e.error, e.severity || 'error', e.product_key || ''])
        ].map(row => row.join(',')).join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `import_errors_${result.table}_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
    };

    return (
        <div className="space-y-6" data-testid="import-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="page-title">Data Import</h1>
                    <p className="text-muted-foreground mt-1">
                        Import product data from CSV or Excel files
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleValidate} disabled={loading}>
                        <Database size={18} className="mr-2" />
                        Validate Data
                    </Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="destructive" disabled={loading}>
                                <Trash size={18} className="mr-2" />
                                Clear All Data
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Clear All Product Data</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This will permanently delete ALL product data including products, pricing, packaging, specifications, and media. User accounts will NOT be affected. This action cannot be undone.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={handleClearData} className="bg-destructive text-destructive-foreground">
                                    Clear All Data
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>

            {/* Validation Result */}
            {validationResult && (
                <div className={`bg-card border rounded-lg p-6 ${validationResult.validation_passed ? 'border-emerald-500' : 'border-amber-500'}`}>
                    <div className="flex items-center gap-3 mb-4">
                        {validationResult.validation_passed ? (
                            <CheckCircle size={24} className="text-emerald-600" weight="fill" />
                        ) : (
                            <Warning size={24} className="text-amber-600" weight="fill" />
                        )}
                        <h2 className="font-semibold text-lg">Data Validation</h2>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        {Object.entries(validationResult.counts).map(([key, value]) => (
                            <div key={key} className="bg-muted/50 rounded-lg p-3">
                                <p className="text-xs text-muted-foreground capitalize">{key.replace('_', ' ')}</p>
                                <p className="text-xl font-semibold font-mono">{value}</p>
                            </div>
                        ))}
                    </div>

                    {validationResult.products_without_pricing?.length > 0 && (
                        <div className="mb-4">
                            <p className="text-sm text-amber-600 font-medium">
                                {validationResult.products_without_pricing.length} products without pricing
                            </p>
                        </div>
                    )}

                    {Object.values(validationResult.orphan_records).some(arr => arr.length > 0) && (
                        <div className="text-sm text-amber-600">
                            <p className="font-medium">Orphan Records Found:</p>
                            <ul className="list-disc ml-4">
                                {validationResult.orphan_records.pricing_without_product?.length > 0 && (
                                    <li>{validationResult.orphan_records.pricing_without_product.length} pricing records without products</li>
                                )}
                                {validationResult.orphan_records.packaging_without_product?.length > 0 && (
                                    <li>{validationResult.orphan_records.packaging_without_product.length} packaging records without products</li>
                                )}
                                {validationResult.orphan_records.specifications_without_product?.length > 0 && (
                                    <li>{validationResult.orphan_records.specifications_without_product.length} specification records without products</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Import Instructions */}
            <div className="bg-muted/30 border border-border rounded-lg p-4">
                <div className="flex items-start gap-3">
                    <Info size={20} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-medium mb-2">Import Order:</p>
                        <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
                            <li><strong>products_master.csv</strong> - Import base products first (required)</li>
                            <li><strong>pricing_master.csv</strong> - Pricing data (linked by product_key)</li>
                            <li><strong>packaging_master.csv</strong> - Packaging data (linked by product_key)</li>
                            <li><strong>specifications_master.csv</strong> - Specifications (linked by product_key)</li>
                            <li><strong>media_master.csv</strong> - Image/document URLs (optional)</li>
                            <li><strong>category_taxonomy.csv</strong> - Category hierarchy (optional)</li>
                            <li><strong>lookup_reference.csv</strong> - Reference mappings (optional)</li>
                        </ol>
                        <p className="mt-2 text-amber-600">
                            <strong>Important:</strong> All tables except category_taxonomy require <code className="bg-muted px-1 rounded">product_key</code> column for linking.
                        </p>
                    </div>
                </div>
            </div>

            {/* Table Selection */}
            <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="font-semibold mb-4">Step 1: Select Table Type</h2>
                <Select value={tableType} onValueChange={(v) => { setTableType(v); resetFile(); }}>
                    <SelectTrigger className="w-full md:w-[400px]" data-testid="table-type-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {TABLE_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                                <div className="flex items-center gap-2">
                                    <span>{t.label}</span>
                                    {t.required && <Badge variant="secondary" className="text-xs">Required</Badge>}
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground mt-2">
                    {TABLE_TYPES.find(t => t.value === tableType)?.description}
                </p>
            </div>

            {/* Upload Section */}
            <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="font-semibold mb-4">Step 2: Upload File</h2>
                
                <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileSelect}
                />

                {!file ? (
                    <div 
                        className="dropzone p-12 text-center cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="file-dropzone"
                    >
                        <Upload size={48} className="mx-auto mb-4 text-muted-foreground" />
                        <p className="font-medium mb-2">Click to upload or drag and drop</p>
                        <p className="text-sm text-muted-foreground">
                            CSV, XLS, or XLSX files supported
                        </p>
                    </div>
                ) : (
                    <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                        {file.name.endsWith('.csv') ? (
                            <FileCsv size={32} className="text-emerald-600" />
                        ) : (
                            <FileXls size={32} className="text-emerald-600" />
                        )}
                        <div className="flex-1">
                            <p className="font-medium">{file.name}</p>
                            <p className="text-sm text-muted-foreground">
                                {preview ? `${preview.total_rows} rows` : 'Processing...'}
                            </p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={resetFile}>
                            <X size={18} />
                        </Button>
                    </div>
                )}
            </div>

            {/* Preview Section */}
            {preview && (
                <div className="bg-card border border-border rounded-lg p-6">
                    <h2 className="font-semibold mb-4">Step 3: Review & Import</h2>
                    
                    {/* Warnings */}
                    <div className="mb-4 space-y-2">
                        {!preview.has_product_key && tableType !== 'category_taxonomy' && tableType !== 'lookup_reference' && (
                            <div className="flex items-center gap-2 text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
                                <X size={18} />
                                <span className="text-sm font-medium">
                                    Missing required column: product_key
                                </span>
                            </div>
                        )}
                        
                        {preview.duplicate_product_keys?.length > 0 && (
                            <div className="flex items-start gap-2 text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md">
                                <Warning size={18} className="flex-shrink-0 mt-0.5" />
                                <div className="text-sm">
                                    <span className="font-medium">Duplicate product_key values found:</span>
                                    <span className="ml-1 font-mono">{preview.duplicate_product_keys.slice(0, 5).join(', ')}</span>
                                    {preview.duplicate_product_keys.length > 5 && <span>... and {preview.duplicate_product_keys.length - 5} more</span>}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Column list */}
                    <div className="mb-4">
                        <p className="text-sm text-muted-foreground mb-2">
                            Detected columns: <span className="font-mono">{preview.columns.join(', ')}</span>
                        </p>
                    </div>

                    {/* Preview table */}
                    <div className="overflow-x-auto border border-border rounded-md mb-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {preview.columns.slice(0, 8).map((col) => (
                                        <TableHead key={col} className="whitespace-nowrap">
                                            {col}
                                            {col === 'product_key' && (
                                                <Badge className="ml-2 bg-primary text-xs">Key</Badge>
                                            )}
                                        </TableHead>
                                    ))}
                                    {preview.columns.length > 8 && (
                                        <TableHead>+{preview.columns.length - 8} more</TableHead>
                                    )}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {preview.preview.map((row, i) => (
                                    <TableRow key={i}>
                                        {preview.columns.slice(0, 8).map((col) => (
                                            <TableCell key={col} className="max-w-[200px] truncate font-mono text-xs">
                                                {row[col]?.toString() || '—'}
                                            </TableCell>
                                        ))}
                                        {preview.columns.length > 8 && <TableCell>...</TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                        Showing first {preview.preview.length} of {preview.total_rows} rows
                    </p>

                    <div className="flex gap-3">
                        <Button 
                            onClick={handleImport} 
                            disabled={loading || (!preview.has_product_key && tableType !== 'category_taxonomy' && tableType !== 'lookup_reference')}
                            data-testid="execute-import-btn"
                        >
                            {loading ? 'Importing...' : 'Start Import'}
                            <ArrowRight size={18} className="ml-2" />
                        </Button>
                        <Button variant="outline" onClick={resetFile}>
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            {/* Import Results */}
            {importResults.length > 0 && (
                <div className="bg-card border border-border rounded-lg">
                    <div className="p-4 border-b border-border">
                        <h2 className="font-semibold">Import History</h2>
                    </div>
                    <Accordion type="single" collapsible className="px-4">
                        {importResults.map((result, idx) => (
                            <AccordionItem key={idx} value={`result-${idx}`}>
                                <AccordionTrigger className="hover:no-underline">
                                    <div className="flex items-center gap-4 w-full">
                                        <Badge variant="outline" className="capitalize">
                                            {result.table.replace('_', ' ')}
                                        </Badge>
                                        <span className="text-sm">{result.filename}</span>
                                        <div className="flex gap-3 ml-auto mr-4 text-sm">
                                            <span className="text-emerald-600">{result.created} created</span>
                                            <span className="text-blue-600">{result.updated} updated</span>
                                            {result.failed > 0 && (
                                                <span className="text-red-600">{result.failed} failed</span>
                                            )}
                                        </div>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <div className="pb-4 space-y-4">
                                        <div className="grid grid-cols-4 gap-4">
                                            <div className="bg-muted/50 rounded-lg p-3">
                                                <p className="text-xs text-muted-foreground">Total Rows</p>
                                                <p className="text-lg font-semibold font-mono">{result.total_rows}</p>
                                            </div>
                                            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3">
                                                <p className="text-xs text-muted-foreground">Created</p>
                                                <p className="text-lg font-semibold font-mono text-emerald-600">{result.created}</p>
                                            </div>
                                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                                                <p className="text-xs text-muted-foreground">Updated</p>
                                                <p className="text-lg font-semibold font-mono text-blue-600">{result.updated}</p>
                                            </div>
                                            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                                                <p className="text-xs text-muted-foreground">Failed</p>
                                                <p className="text-lg font-semibold font-mono text-red-600">{result.failed}</p>
                                            </div>
                                        </div>

                                        {result.errors?.length > 0 && (
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <p className="text-sm font-medium text-red-600">
                                                        Errors ({result.errors.length})
                                                    </p>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => downloadErrorReport(result)}
                                                    >
                                                        <DownloadSimple size={16} className="mr-2" />
                                                        Download Report
                                                    </Button>
                                                </div>
                                                <div className="max-h-48 overflow-y-auto border border-border rounded-md">
                                                    <Table>
                                                        <TableHeader>
                                                            <TableRow>
                                                                <TableHead className="w-16">Row</TableHead>
                                                                <TableHead>Error</TableHead>
                                                                <TableHead className="w-24">Severity</TableHead>
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {result.errors.slice(0, 20).map((err, i) => (
                                                                <TableRow key={i}>
                                                                    <TableCell className="font-mono">{err.row}</TableCell>
                                                                    <TableCell className="text-sm">{err.error}</TableCell>
                                                                    <TableCell>
                                                                        <Badge variant={err.severity === 'critical' ? 'destructive' : 'secondary'}>
                                                                            {err.severity || 'error'}
                                                                        </Badge>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </div>
                                                {result.errors.length > 20 && (
                                                    <p className="text-xs text-muted-foreground mt-2">
                                                        Showing first 20 of {result.errors.length} errors
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {result.orphans?.length > 0 && (
                                            <div className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md">
                                                <p className="font-medium">Orphan Records: {result.orphans.length}</p>
                                                <p className="text-xs">Records with product_key not found in products_master</p>
                                            </div>
                                        )}
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            )}
        </div>
    );
}
